import { minidoc, on } from 'minidoc-editor';
import { useDisposableMemo } from 'client/lib/hooks';
import { uploadCount } from '../media-card/media-uploader';
import { useEffect, useRef } from 'preact/hooks';
import { EditorMiddleware, EditorState, MinidocOptions } from 'minidoc-editor/dist/types/types';
import { onBeforeUnload } from '@components/router/before-unload';
import { useIntl } from 'shared/intl/use-intl';
import { Internationalize } from 'shared/intl';
import { useDidUpdateEffect } from 'client/utils/use-did-update-effect';

export function useMinidoc<T extends Array<EditorMiddleware>>(
  opts: Omit<MinidocOptions<T>, 'middleware'> & {
    middleware?: (intl: Internationalize) => MinidocOptions<T>['middleware'];
    autoFocus?: boolean;
    onChange(doc: string, selection: EditorState['selection']): void;
    selection?: EditorState['selection'];
    version?: number;
  },
) {
  const intl = useIntl();
  const result = useDisposableMemo(() => {
    const editor = minidoc({
      ...opts,
      middleware: opts.middleware?.(intl),
    });
    const baseDispose = editor.dispose;
    const removeLeaveWarning = onBeforeUnload(() => {
      if (uploadCount(editor) <= 0) {
        return;
      }
      return `You have pending uploads. If you leave this page, they will be lost.`;
    });

    editor.dispose = () => {
      baseDispose.call(editor);
      removeLeaveWarning();
    };

    if (opts.autoFocus) {
      setTimeout(() => (editor.root as any).focus());
    }

    return editor;
  }, []);

  const onChange = useRef(opts.onChange);
  onChange.current = opts.onChange;
  useEffect(() => {
    return on(result.root, 'mini:change', () => {
      onChange.current(result.state.doc, result.state.selection);
    });
  });
  useDidUpdateEffect(() => {
    result.setState({ doc: opts.doc, selection: opts.selection });
  }, [opts.version]);

  return result;
}
